import { ClassType, EntityType, fileDownload, formatDays } from '@frontend/common';
import { StatusBadge } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { DocumentClient } from '@frontend/document/api';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { JobClient } from '@frontend/report/api';
import { scheduleRequest } from '@frontend/report/events';
import { Job, JobStateName, Schedule } from '@frontend/report/types';
import React from 'react';

import { useScheduleDetailModal } from '../../analytics/schedule/schedule-detail-modal/schedule-detail-modal.controller';
import { UpdateReceiversModal } from '../../analytics/schedule/update-receivers-modal/update-receivers-modal.component';
import { MultiEntityButton } from '../../components/multi-entity-button/multi-entity-button.component';
import { DefaultContainer } from '../../modal/detail-modal/default-container/default-container';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { ReactiveDetailRegistryEntry } from '../../modal/reactive-detail-shell/reactive-detail.types';
import { useAppDispatch } from '../../redux/store';

export const ScheduleDetailsCard = ({ schedule }: { schedule: Schedule }) => (
    <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={schedule}
            cols={2}
            order={['report_id', 'name', 'status', 'every_days', 'creation_timestamp', 'update_timestamp']}
            keyOverwrite={
                new Map([
                    ['status', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                    ['every_days', CommonMessage.OBJECTS.SCHEDULE.RUNS_EVERY]
                ])
            }
            valueOverwrite={
                new Map<string, (obj: Schedule) => React.ReactNode>([
                    [
                        'status',
                        (obj) => (
                            <StatusBadge
                                value={obj.enabled ? CommonMessage.OBJECTS.COMMON_FIELDS.ENABLED : CommonMessage.OBJECTS.COMMON_FIELDS.DISABLED}
                                colorMap={
                                    new Map([
                                        [[CommonMessage.OBJECTS.COMMON_FIELDS.ENABLED], ClassType.SUCCESS],
                                        [[CommonMessage.OBJECTS.COMMON_FIELDS.DISABLED], ClassType.WARNING]
                                    ])
                                }
                            />
                        )
                    ],
                    ['every_days', (obj) => <span>{formatDays(obj.every_days)}</span>],
                    [
                        'report_id',
                        (obj) => (
                            <PlatformObjectBadge
                                entityType={EntityType.REPORT}
                                id={obj.report_id}
                            />
                        )
                    ]
                ])
            }
        />
    </div>
);

export const ScheduleReceiversSection = ({ schedule }: { schedule: Schedule }) => {
    const viewProps = useScheduleDetailModal();
    return (
        <DefaultContainer
            update={{ onUpdate: () => viewProps.openModal(viewProps.updateReceiversModalId, () => <UpdateReceiversModal schedule={schedule} />) }}
            title='Receivers'
            content={
                <div className='flex flex-wrap gap-2'>
                    {schedule.receivers?.map((receiver) => (
                        <PlatformObjectBadge
                            entityType={EntityType.USER}
                            id={receiver}
                            key={receiver}
                        />
                    ))}
                </div>
            }
        />
    );
};

export const ScheduleJobsSection = ({ schedule }: { schedule: Schedule }) => {
    const dispatch = useAppDispatch();
    return (
        <ReactiveObjectList<Job>
            fetch={(arg) => JobClient.fetchJobs({ ...arg, schedule_id: schedule.id })}
            columns={{
                columns: ['creation_timestamp', 'receivers', 'download'],
                headerOverwrite: new Map([
                    ['creation_timestamp', CommonMessage.OBJECTS.JOB.GENERATED_AT],
                    ['download', <></>]
                ]),
                customCells: new Map([
                    [
                        'receivers',
                        (obj) => {
                            if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                                return (
                                    <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    </td>
                                );
                            }
                            return (
                                <td
                                    key={obj.row.original.receivers[0]}
                                    className={defaultCellClassName}>
                                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                                        <PlatformObjectBadge
                                            id={obj.row.original.receivers[0]}
                                            entityType={EntityType.USER}
                                            key={obj.row.original.receivers[0]}
                                        />
                                        {obj.row.original.receivers.length > 1 && (
                                            <MultiEntityButton<string>
                                                entities={obj.row.original.receivers.slice(1)}
                                                render={(id) => (
                                                    <PlatformObjectBadge
                                                        id={id}
                                                        entityType={EntityType.USER}
                                                        key={id}
                                                    />
                                                )}
                                            />
                                        )}
                                    </div>
                                </td>
                            );
                        }
                    ],
                    [
                        'download',
                        (obj) => (
                            <td className={defaultCellClassName + ' w-10'}>
                                {obj.row.original.output && (
                                    <button
                                        className='align-center cursor-pointer items-center'
                                        disabled={obj.row.original.status !== JobStateName.PROCESSED}
                                        onClick={async () => {
                                            if (obj.row.original.status !== JobStateName.PROCESSED) return;
                                            const documents = await DocumentClient.fetchDocuments({
                                                entity_id: obj.row.original.id,
                                                entity_type: EntityType.REPORT_JOB
                                            });
                                            if (documents.count === 0) return;
                                            const doc = documents.results[0];
                                            const blob = await DocumentClient.fetchDocumentFile(doc.account_id, doc.id);
                                            if (!blob) return;
                                            fileDownload(blob, doc.name);
                                        }}>
                                        <ICONS.BUTTON.DOWNLOAD
                                            className={`size-5 ${obj.row.original.status === JobStateName.PROCESSED ? 'text-foreground-default hover:text-brand-primary' : 'text-brand-muted hover:cursor-not-allowed'}`}
                                        />
                                    </button>
                                )}
                            </td>
                        )
                    ]
                ])
            }}
            dispatch={dispatch}
            type={EntityType.JOB}
            eventListener={EntityType.JOB}
            tableProps={{ defaultHeight: true, hideFooter: true, headerItemClassName: 'min-w-0!' }}
        />
    );
};

export const scheduleReactiveDetail: ReactiveDetailRegistryEntry<Schedule> = {
    entityType: EntityType.SCHEDULE,
    resolveFunction: scheduleRequest,
    modalClassName: 'w-full lg:w-2/3 max-w-[900px]',
    title: ({ object }) => (
        <div className='flex gap-2'>
            <span>{CommonMessage.OBJECTS.SCHEDULE.DEFAULT}:</span>
            <span>{object.name}</span>
        </div>
    ),
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) => (
                <>
                    <ScheduleDetailsCard schedule={view.object} />
                    <ScheduleReceiversSection schedule={view.object} />
                    <ScheduleJobsSection schedule={view.object} />
                </>
            )
        }
    ]
};
